import {toJSONLocal, toLocalDate} from '../helpers/helpers';
import {IMeal, Meal, MealType} from './Meal';
import {TypicalMeal} from './User';
import {Recipe} from './Recipe';
import {CategoryStatistics} from './Statistics';
import {Questionnaire} from '../questionnaire/questionnaireClassesAndEnums';
import {Food} from "./food";
import {FoodProduct} from "./FoodProduct";

export class TrackedDay {
    private readonly _typicalMeals: TypicalMeal[];
    private get hasTypicalMeals(): boolean { return this._typicalMeals && this._typicalMeals.length > 0; }
    meals: Meal[] = [];
    day: Date;
    get calories(): number { return this.getTotalCalories(); }
    get emissions(): number { return this.getTotalEmissions(); }
    get isEmpty(): boolean { return this.meals.filter(m => !m.isEmpty).length === 0; }
    get edited(): boolean { return this.meals.filter(m => m.edited).length > 0; }
    get allMealsEnabled(): boolean { return this.meals.filter(m => m.disabled).length === 0; }
    get enabledMeals(): Meal[] { return this.meals.filter(m => !m.disabled); }
    get carbs(): number { return this.meals.reduce((p: number, c) => p + c.carbs, 0); }
    get fat(): number { return this.meals.reduce((p: number, c) => p + c.fat, 0); }
    get protein(): number { return this.meals.reduce((p: number, c) => p + c.protein, 0); }
    get macrosKnown(): boolean { return this.meals.every(m => m.macrosKnown); }
    get amountInGram(): number { return this.meals.reduce((p: number, c) => p + c.amountInGram, 0); }
    private get amountIn100g(): number { return this.amountInGram / 100; }
    get carbsPer100g(): number { return this.amountIn100g ? this.carbs / this.amountIn100g : 0; }
    get fatPer100g(): number { return this.amountIn100g ? this.fat / this.amountIn100g : 0; }
    get proteinPer100g(): number { return this.amountIn100g ? this.protein / this.amountIn100g : 0; }
    get caloriesPer100g(): number { return this.amountIn100g ? this.calories / this.amountIn100g : 0; }

    // Get a specific meal
    get breakfast(): Meal { return this.getSpecificMealType(MealType.Breakfast); }
    get lunch(): Meal { return this.getSpecificMealType(MealType.Lunch); }
    get dinner(): Meal { return this.getSpecificMealType(MealType.Dinner); }
    get snack(): Meal { return this.getSpecificMealType(MealType.Snack); }

    // Auto-fill
    private _autofilled = false;
    get autofilled(): boolean { return this._autofilled; }
    private _autofillCalories = 0;
    get autofillCalories(): number { return this._autofillCalories; }
    private _autofillEmissions = 0;
    get autofillEmissions(): number { return this._autofillEmissions; }

    // Statistics
    get categoryStatisticsEmissions(): CategoryStatistics {
        if (this.autofilled) {
            return this.setUpAutofillQuestionnaire().categoryStatisticsEmissions;
        }
        const ret = new CategoryStatistics();
        this.meals.forEach(meal => {
            ret.addMany(meal.categoryStatisticsEmissions.categories);
        });
        return ret;
    }
    get categoryStatisticsCalories(): CategoryStatistics {
        if (this.autofilled) {
            return this.setUpAutofillQuestionnaire().categoryStatisticsCalories;
        }
        const ret = new CategoryStatistics();
        this.meals.forEach(meal => {
            ret.addMany(meal.categoryStatisticsCalories.categories);
        });
        return ret;
    }

    constructor(i?: ITrackedDay, typicalMeals?: TypicalMeal[], recipes?: Recipe[]) {
        // temporary loud failing until all places with new tracked days are resolved
        if (!i && !typicalMeals) {
            throw new Error('Tracked days must be created with either an interface or typical meals!');
        }
        if (i) {
            this.day = toLocalDate(i.day);
            if (i.meals){
                i.meals.forEach(meal => {
                    this.meals.push(new Meal(meal, undefined, recipes));
                });
            }
            if (i.autofilled) { this._autofilled = i.autofilled; }
            if (i.autofillCalories) { this._autofillCalories = i.autofillCalories; }
            if (i.autofillEmissions) { this._autofillEmissions = i.autofillEmissions; }
            if (i.typicalMeals) { this._typicalMeals = i.typicalMeals; }
        }
        if (typicalMeals && (!this.hasTypicalMeals)) {
            this._typicalMeals = typicalMeals;
            this.fillUpMeals(typicalMeals);
        }

        // This should only happen in a scenario right after account creation, where the typical meals are not created yet,
        // but the dashboard is already loaded in the background (while onboarding is running), creating a TrackedDay without typical meals
        if (this.meals.length === 0) {
            this.generateEmptyDefaultMeals();
        }
    }

    public addMeal(meal: Meal){
        this.meals.push(meal);
    }

    // Method returns the index in meals array for a given meal-type
    public getIndexOfMeal(mealType: MealType): number {
        return this.meals.findIndex(x => x.type === mealType);
    }

    public toInterface(): ITrackedDay {
        const iMeals: IMeal[] = [];
        this.meals.forEach(meal => {
            iMeals.push(meal.toInterface());
        });
        return {meals: iMeals, day: toJSONLocal(this.day), autofilled: this.autofilled, autofillCalories: this.autofillCalories,
            autofillEmissions: this.autofillEmissions, typicalMeals: this._typicalMeals};
    }

    public copy(): TrackedDay {
        return new TrackedDay(this.toInterface());
    }

    // Autofill logic
    public autofill(calories: number, emissions: number): void {
        this._autofilled = true;
        this._autofillCalories = calories;
        this._autofillEmissions = emissions;
    }

    // This method is currently triggered by the user's store tracked day function.
    // TODO for later: make this private, refactor Meal class in a way that it raises change events and call this method
    //  on every meal change event
    public mealChanged(): void {
        if (this.emissions > 0) { this.resetAutofill(); }
    }

    public importTrackedDay(importTrackedDay: TrackedDay, overwrite = false): void {
        if (!importTrackedDay.meals || importTrackedDay.meals.length === 0) { return; }
        importTrackedDay.meals.forEach(importMeal => {
            let meal = this.meals.find(m => m.type === importMeal.type);
            if (!meal) {
                meal = new Meal(undefined, this._typicalMeals.find(t => t.type === importMeal.type));
                this.meals.push(meal);
            }
            meal.importMeal(importMeal, overwrite);
        });
    }

    public updateMacros(foods: Food[], foodProducts: FoodProduct[]): void {
        this.meals.forEach(meal => {
            if (!meal.macrosKnown) { meal.updateMacros(foods, foodProducts); }
        });
    }

    public getSpecificMealType(type: MealType): Meal {
        return  this.meals.find(m => m.type === type);
    }

    private getTotalCalories(): number{
        if (this.autofilled) { return this.autofillCalories; }
        let calories = 0;
        this.meals.forEach(meal => {
            if (!meal.disabled) { calories += meal.calories; }
        });
        return calories;
    }

    private getTotalEmissions(): number{
        if (this.autofilled) { return this.autofillEmissions; }
        let emissions = 0;
        this.meals.forEach(meal => {
            if (!meal.disabled) { emissions += meal.emissions; }
        });
        return emissions;
    }

    // Method makes sure that all meals from a meal type list (usually user master data) is available
    private fillUpMeals(typicalMeals: TypicalMeal[]){
        typicalMeals.forEach(typicalMeal => {
            let found = false;
            this.meals.forEach(meal => {
                if (meal.type === typicalMeal.type){
                    found = true;
                }
            });
            if (!found) { this.addMeal(new Meal(undefined, typicalMeal)); }
        });
    }

    private resetAutofill(): void {
        if (!this.edited) { return; }
        this._autofilled = false;
        this._autofillCalories = 0;
        this._autofillEmissions = 0;
    }

    private setUpAutofillQuestionnaire(): Questionnaire {
        const questionnaire = new Questionnaire();
        questionnaire.calories = this.autofillCalories;
        questionnaire.setDefaultAnswers();
        return questionnaire;
    }
    
    private generateEmptyDefaultMeals(): void {
        this.meals = [];
        this.meals.push(Meal.EmptyBreakfast());
        this.meals.push(Meal.EmptyLunch());
        this.meals.push(Meal.EmptyDinner());
        this.meals.push(Meal.EmptySnack());
    }
}

export interface ITrackedDay {
    typicalMeals: TypicalMeal[];
    meals: IMeal[];
    day: string;
    autofilled?: boolean;
    autofillCalories?: number;
    autofillEmissions?: number;
}
